import { Model } from '@vuex-orm/core'
import config from '@/utils/config'
import AppState from '@/utils/app.state'
import { getLanguage } from '@/lang/helper'
import { Request } from '@vuex-orm/plugin-axios'
import HotelSearch from './hotels/HotelSearch'
import Profile from '@/models/user/Profile'

/* State for the singletone */
type Hook = (result: any) => void
const state = {
  isLoadingFinished: false,
  hooks: [] as Hook[],
  savedResult: null
}

export default class PaymentMethods extends Model {
  declare id: string
  declare namespace: string
  declare logoUri: string
  declare title: string
  declare description: string
  declare transactionId: number
  declare totalAmount: number
  declare active: boolean
  declare isHidden: boolean
  declare status?: string
  declare lightboxJavascriptUrl: string
  declare altered: boolean

  static entity = 'paymentMethods'

  static fields() {
    return {
      id: this.attr(null).nullable(),
      namespace: this.string(''),
      logoUri: this.string(''),
      title: this.string(''),
      description: this.string(''),
      transactionId: this.number(0),
      totalAmount: this.number(0),
      active: this.boolean(false),
      isHidden: this.boolean(false),
      status: this.string(null).nullable(),
      lightboxJavascriptUrl: this.string(''),
      altered: this.attr(null),
    }
  }

  /**
   * Emits the `loadingFinished` event to all the observers
   */
  static notifyLoadingFinished(result: any) {
    state.isLoadingFinished = true
    state.savedResult = result

    for (const hook of state.hooks) {
      hook(result)
    }

    state.hooks = []
  }

  /**
   * Adds an observer for the `loadingFinished` event.
   * Immediately executes if the event has been triggered already
   * @param {Function} callback
   */
  static onLoaded(callback: Hook) {
    if (typeof callback !== 'function') {
      console.warn('Provided callback is not a function')
      return
    }

    if (state.isLoadingFinished) callback(state.savedResult)
    else state.hooks.push(callback)
  }

  static apiConfig = {
    baseURL: config.bookingApi as string,

    actions: {
      async fetch(this: Request, arrivalDate: string, customerCountry?: string, isFullPayment?: boolean) {
        /* Get the language code in upper case */
        const languageCode = getLanguage(AppState.locale as string).toUpperCase()

        /* Clean up the storage */
        PaymentMethods.delete((method: PaymentMethods) => method.status !== 'success')

        /* Is full payment for the booking */
        const isFullAccommodationPayment = typeof isFullPayment === 'boolean'
          ? isFullPayment
          : HotelSearch.instance.fullPayment === 0

        /* IBEV4-800 check if user is logged in. If true, send partnerCustomerId to endpoint */
        const profile = Profile.query().first();
        const partnerCustomerId = profile && profile.hasRekaNetAccount ? profile.addressId : null

        /* Call API */
        return this.get(
          '/Payments/PaymentMethods/All',
          {
            params: {
              languageCode,
              customerCountry: customerCountry || languageCode,
              arrivalDate,
              isFullAccommodationPayment,
              partnerCustomerId: partnerCustomerId
            }
          }
        )
      },

      pay(this: Request, amount = 0, currency = 'EUR', namespace: string, id: string, initPaymentToken: string) {
        const url = new URL(location.href)
        url.searchParams.delete('status')
        url.searchParams.delete('reference')

        return this.post(
          '/Payments/InitPayment',
          {
            paymentMethodNamespace: namespace,
            paymentMethodId: parseInt(id),
            amount,
            currency,
            language: getLanguage(AppState.locale as string).toUpperCase(),
            callbackUrl: url.href,
            callBackReference: parseInt(id),
            initPaymentToken
          },
          {
            dataTransformer: ({ data }) => {
              return { id, ...data }
            }
          }
        )
      },

      validate(this: Request, transactionId: string) {
        return this.get(`/Payments/Transactions/${transactionId}/State`, { save: false })
      }
    }
  }
}
